import { cp, mkdir, readFile, writeFile } from 'node:fs/promises'
import { fileURLToPath } from 'node:url'
import { isAbsolute, relative, resolve } from 'node:path'

const PACKAGE_ROOT = fileURLToPath(new URL('../', import.meta.url))
const SKILLS_ROOT = resolve(PACKAGE_ROOT, 'skills')
const VERSION_STAMP_PATTERN = /<!--\s*installed-skill-version:\s*__SKILL_VERSION__\s*-->/g

const SKILL_CATALOG = {
  agentbuilder: {
    description:
      'Compose Standard Agents from models, prompts, tools, hooks, APIs, and subagents using markdown-authored guidance.',
  },
} as const

export type SkillName = keyof typeof SKILL_CATALOG

export interface SkillDefinition {
  name: SkillName
  description: string
  directory: string
  entryFile: string
}

export interface CopySkillOptions {
  overwrite?: boolean
}

function assertSkillName(name: string): asserts name is SkillName {
  if (!(name in SKILL_CATALOG)) {
    throw new Error(
      `Unknown Standard Agents skill "${name}". Expected one of: ${Object.keys(SKILL_CATALOG).join(', ')}.`
    )
  }
}

function resolveInside(baseDir: string, segments: readonly string[]): string {
  const candidate = resolve(baseDir, ...segments)
  const relativePath = relative(baseDir, candidate)

  if (relativePath.startsWith('..') || isAbsolute(relativePath)) {
    throw new Error('Skill paths must stay within the skill directory.')
  }

  return candidate
}

export function listSkills(): SkillDefinition[] {
  return (Object.keys(SKILL_CATALOG) as SkillName[]).map((name) => getSkill(name))
}

export function getSkill(name: SkillName): SkillDefinition {
  const definition = SKILL_CATALOG[name]
  const directory = resolveSkillDir(name)

  return {
    name,
    description: definition.description,
    directory,
    entryFile: resolveSkillFile(name, 'SKILL.md'),
  }
}

export function resolveSkillDir(name: SkillName): string {
  assertSkillName(name)
  return resolve(SKILLS_ROOT, name)
}

export function resolveSkillFile(name: SkillName, ...segments: string[]): string {
  return resolveInside(resolveSkillDir(name), segments)
}

let cachedVersion: Promise<string> | null = null

export function skillPackageVersion(): Promise<string> {
  cachedVersion ??= readFile(resolve(PACKAGE_ROOT, 'package.json'), 'utf8').then(
    (manifest) => JSON.parse(manifest).version as string
  )

  return cachedVersion
}

async function stampSkillVersion(content: string): Promise<string> {
  if (!content.includes('__SKILL_VERSION__')) {
    return content
  }

  const version = await skillPackageVersion()

  return content.replace(
    VERSION_STAMP_PATTERN,
    `<!-- installed-skill-version: ${version} -->`
  )
}

export async function readSkillFile(
  name: SkillName,
  ...segments: string[]
): Promise<string> {
  const target = segments.length === 0
    ? resolveSkillFile(name, 'SKILL.md')
    : resolveSkillFile(name, ...segments)

  return stampSkillVersion(await readFile(target, 'utf8'))
}

export async function copySkill(
  name: SkillName,
  destinationRoot: string,
  options: CopySkillOptions = {}
): Promise<string> {
  const sourceDir = resolveSkillDir(name)
  const targetRoot = resolve(destinationRoot)
  const targetDir = resolve(targetRoot, name)

  await mkdir(targetRoot, { recursive: true })
  await cp(sourceDir, targetDir, {
    recursive: true,
    force: options.overwrite ?? false,
    errorOnExist: !(options.overwrite ?? false),
  })

  const entryFile = resolve(targetDir, 'SKILL.md')
  const entryContent = await readFile(entryFile, 'utf8')
  await writeFile(entryFile, await stampSkillVersion(entryContent), 'utf8')

  return targetDir
}

export const skillPackageRoot = PACKAGE_ROOT
export const skillsRoot = SKILLS_ROOT
